import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Event,
  Person,
  MoreVert,
  Logout,
  Business,
  HourglassEmpty,
  CheckCircle,
  Cancel,
  Info,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { stateAssociationAPI } from '../api/stateAssociation.api';
import { useAuth } from '../context/AuthContext';
import AthleteEventsManagement from '../components/stateAssociation/AthleteEventsManagement';
import ProfileSection from '../components/stateAssociation/ProfileSection';

/**
 * Styled Components - Matching Athlete Design Theme
 */
const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const StyledTabs = styled(Tabs)({
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    minHeight: 64,
    fontFamily: "'Oswald', sans-serif",
  },
  '& .Mui-selected': {
    color: '#291e6c',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#291e6c',
    height: 3,
  },
});

const StyledButton = styled(Button)({
  padding: '10px 24px',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const PendingContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  maxWidth: 600,
  margin: '0 auto',
}));

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const StateAssociationDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await stateAssociationAPI.getProfile();
      setProfile(response.data || response);
    } catch (error) {
      toast.error('Failed to load profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    toast.info('Logged out successfully');
    navigate('/state-association/login');
  };

  if (loading) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#291e6c' }} />
        </Box>
      </PageWrapper>
    );
  }

  if (!profile) {
    return (
      <PageWrapper>
        <Container maxWidth="md">
          <StyledPaper>
            <Box textAlign="center" py={6}>
              <Typography variant="h6" gutterBottom>
                Unable to load profile
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Please try logging in again.
              </Typography>
              <StyledButton
                variant="contained"
                onClick={handleLogout}
                sx={{
                  backgroundColor: '#291e6c',
                  '&:hover': { backgroundColor: '#1a1447' },
                }}
              >
                Go to Login
              </StyledButton>
            </Box>
          </StyledPaper>
        </Container>
      </PageWrapper>
    );
  }

  // Check if status is pending
  const isPending = profile.verificationStatus === 'pending';
  const isRejected = profile.verificationStatus === 'rejected';
  const isApproved = profile.verificationStatus === 'approved' || profile.verificationStatus === 'verified';

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        {/* Header */}
        <StyledPaper>
          <HeaderSection>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  bgcolor: '#291e6c',
                  width: 56,
                  height: 56,
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                }}
              >
                {profile?.name?.[0]?.toUpperCase() || 'S'}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {profile?.name || 'State Association'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Registration No: {profile?.registrationNumber || '-'}
                </Typography>
                <Box mt={1}>
                  {isPending && (
                    <Chip
                      icon={<HourglassEmpty />}
                      label="Pending Verification"
                      color="warning"
                      size="small"
                    />
                  )}
                  {isApproved && (
                    <Chip
                      icon={<CheckCircle />}
                      label="Verified"
                      color="success"
                      size="small"
                    />
                  )}
                  {isRejected && (
                    <Chip
                      icon={<Cancel />}
                      label="Rejected"
                      color="error"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box>
              <IconButton onClick={handleMenuOpen}>
                <MoreVert />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleLogout}>
                  <Logout fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </HeaderSection>
        </StyledPaper>

        {/* Pending Status View */}
        {isPending && (
          <StyledPaper>
            <PendingContainer>
              <HourglassEmpty
                sx={{ fontSize: 80, color: '#ff9800', mb: 3 }}
              />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Verification Pending
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your state association registration is currently under review by the admin team.
              </Typography>
              <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  What happens next?
                </Typography>
                <Typography variant="body2">
                  • Admin will verify your documents and information
                  <br />
                  • You will receive an email notification once verified
                  <br />
                  • After approval, you can access the full dashboard
                  <br />• Typical verification time: 1-3 business days
                </Typography>
              </Alert>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Need assistance? Contact admin support.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                <StyledButton
                  variant="outlined"
                  onClick={() => navigate('/state-association/profile')}
                  sx={{ borderColor: '#291e6c', color: '#291e6c' }}
                >
                  View Profile
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={handleLogout}
                  sx={{
                    backgroundColor: '#291e6c',
                    '&:hover': { backgroundColor: '#1a1447' },
                  }}
                >
                  Logout
                </StyledButton>
              </Stack>
            </PendingContainer>
          </StyledPaper>
        )}

        {/* Rejected Status View */}
        {isRejected && (
          <StyledPaper>
            <PendingContainer>
              <Cancel sx={{ fontSize: 80, color: '#f44336', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Verification Rejected
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Unfortunately, your state association registration has been rejected.
              </Typography>
              <Alert severity="error" sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Rejection Reason:
                </Typography>
                <Typography variant="body2">
                  {profile?.rejectionReason || 'Please contact admin for details.'}
                </Typography>
              </Alert>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Please contact admin support for more information or to reapply.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
                <StyledButton
                  variant="outlined"
                  onClick={() => navigate('/state-association/profile')}
                  sx={{ borderColor: '#291e6c', color: '#291e6c' }}
                >
                  View Profile
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={handleLogout}
                  sx={{
                    backgroundColor: '#291e6c',
                    '&:hover': { backgroundColor: '#1a1447' },
                  }}
                >
                  Logout
                </StyledButton>
              </Stack>
            </PendingContainer>
          </StyledPaper>
        )}

        {/* Approved/Verified Status - Full Dashboard */}
        {isApproved && (
          <>
            <StyledPaper>
              <StyledTabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                <Tab icon={<DashboardIcon />} label="Overview" iconPosition="start" />
                <Tab icon={<Event />} label="Event Applications" iconPosition="start" />
                <Tab icon={<Person />} label="Profile" iconPosition="start" />
              </StyledTabs>
            </StyledPaper>

            <TabPanel value={activeTab} index={0}>
              <StyledPaper>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Dashboard Overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Welcome to your State Association dashboard. Manage athlete event applications
                  and view your profile.
                </Typography>
                <Box mt={3}>
                  <Alert severity="success" icon={<CheckCircle />}>
                    Your state association is verified and active!
                  </Alert>
                </Box>
              </StyledPaper>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <AthleteEventsManagement />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <ProfileSection profile={profile} onProfileUpdate={fetchProfile} />
            </TabPanel>
          </>
        )}
      </Container>
    </PageWrapper>
  );
};

export default StateAssociationDashboard;
